import { createWithEqualityFn } from 'zustand/traditional';
import { immer } from 'zustand/middleware/immer';
import { subscribeWithSelector } from 'zustand/middleware';
import { IHash } from 'types/base.types';
import { TAnimationFlow } from '@scenes/HomeHub/homehub.types';
import { isValidNonEmptyArray } from '@utils/array';
import { useShallow } from 'zustand/react/shallow';

export type HomeHubState = {
  loading: boolean;
  showSurveyFooter: boolean;
  animation: TAnimationFlow;
  userFlow: {
    firstName: string;
    email: string;
  };
  hasDirtyAnswerSelections: boolean;
  selectedAnswersClientTransient: any; // TODO: fix
  actions: {
    setLoading: (loading: boolean) => void;
    setShowSurveyFooter: (showSurveyFooter: boolean) => void;
    setAttributeInUserFlow: (attributes: IHash<string>) => void;
    setAnimationDirection: (animation: TAnimationFlow) => void;
    setHasDirtyAnswerSelections: (hasDirtySelections: boolean) => void;
    setSelectedAnswersClientTransient: (selectedAnswersClientTransient: any) => void;
    setAnimationIsFinished: (animationIsFinished: boolean) => void;
  };
};

/* Initial HomeHub Client Side State/Store */
export const useHomeHubStore = createWithEqualityFn<HomeHubState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      /* default loading false */
      loading: false,
      /* survey footer */
      showSurveyFooter: true,
      animation: {
        direction: 'next',
        query: '',
        isFinished: false,
        isStarted: false,
      },
      userFlow: {
        firstName: '',
        email: '',
      },
      hasDirtyAnswerSelections: false,
      selectedAnswersClientTransient: [],

      /* -------------------------------------------------------------------- */
      /* -------------------------- ACTIONS --------------------------------- */
      /* -------------------------------------------------------------------- */
      // note: we could create a generic setter for all of these, but I like the explicitness of each one
      actions: {
        setLoading: (loading: boolean) => {
          set(state => {
            state.loading = loading;
          });
        },

        setShowSurveyFooter: showSurveyFooter => {
          /* Toggle if no value is passed */
          const resolvedState = typeof showSurveyFooter === 'boolean' ? showSurveyFooter : !get().showSurveyFooter;
          set(state => {
            state.showSurveyFooter = resolvedState;
          });
        },

        setAttributeInUserFlow: attributes => {
          set(state => {
            Object.entries(attributes).forEach(([key, value]) => ((state.userFlow as Record<string, unknown>)[key] = value));
          });
        },

        setAnimationDirection: animation => {
          set(state => {
            state.animation = { ...get().animation, ...animation };
          });
        },

        setHasDirtyAnswerSelections: hasDirtyAnswerSelections => {
          set(() => ({ hasDirtyAnswerSelections }));
        },

        setSelectedAnswersClientTransient: selectedAnswersClientTransient => {
          if (
            (Array.isArray(selectedAnswersClientTransient) && isValidNonEmptyArray(selectedAnswersClientTransient)) ||
            (!Array.isArray(selectedAnswersClientTransient) && selectedAnswersClientTransient)
          ) {
            get().actions.setHasDirtyAnswerSelections(true);
          }
          set(state => {
            const setValue = Array.isArray(selectedAnswersClientTransient) ? selectedAnswersClientTransient : [selectedAnswersClientTransient];
            state.selectedAnswersClientTransient = setValue;
          });
        },

        setAnimationIsFinished: animationIsFinished => {
          set(state => {
            state.animation.isFinished = animationIsFinished;
          });
        },
      },
    })),
  ),
);

/* ---------------------------------------------------- */
/* ----------  Export out our selectors  -------------- */
/* ---------------------------------------------------- */

/* All actions Selector */
export const useHomeHubStoreActions = () => useHomeHubStore(state => state.actions);
export const useHomeHubStoreAnimationIsFinished = () => useHomeHubStore(state => state.animation.isFinished);
export const useHomeHubStoreAnimationDirection = () => useHomeHubStore(state => state.animation.direction);
export const useHomeHubStoreHasDirtyAnswerSelections = () => useHomeHubStore(state => state.hasDirtyAnswerSelections);
export const useHomeHubStoreSelectedAnswersClientTransient = () => useHomeHubStore(state => state.selectedAnswersClientTransient);
export const useHomeHubStoreShowSurveyFooter = () => useHomeHubStore(state => state.showSurveyFooter);
export const useHomeHubStoreUserFlowAttributes = () =>
  useHomeHubStore(useShallow(({ userFlow }) => ({ userEmail: userFlow?.email, firstName: userFlow?.firstName })));
